import { CompositionService } from "./CompositionService";
import { CompositionPersistenceService } from "./CompositionPersistenceService";
import type { ResolveAssetUrlFn, ResolveEnhancedAudioUrlFn } from "./CompositionSessionService";
import { Timestamp } from "../../engine/models";
import { Input, UrlSource, ALL_FORMATS } from "mediabunny";
import type { MediaSourceRef } from "../../engine/sources/media-source";
import { VideoClip } from "../../engine/clips/video/video";
import { AudioClip } from "../../engine/clips";
import { ImageClip } from "../../engine/clips/image/image";
import { ScriptLayerClip } from "../../engine/clips/script/script";
import { CaptionsClip } from "../../engine/clips/captions";
import { TextClip } from "../../engine/clips/text";
import { PlaceholderClip } from "../../engine/clips/placeholder/placeholder";
import type {
    ScriptLayerInputConfig,
    ScriptLayerCut,
    ScriptLayerGap,
    ScriptLayerGapInsertion,
    ScriptLayerSplit,
    SpeakerOverride,
    SpeakerMuteRange,
    ScriptLayerSourceSegment,
} from "../../engine/clips/script/script.interfaces";
import { CompositionStateStore } from "../store/CompositionStateStore";
import { snapCanvasSizeToPreset } from "../../utils/canvasSizing";

type PersistedLayer = {
    layerId: string;
    name: string | null;
    type: string;
    clips: any[];
};

/**
 * Firestore -> Engine restore for the main editor composition.
 * Kept separate from CompositionService to avoid import cycles with editor UI code.
 */
export class CompositionRestoreService {
    private static fromFirestoreSceneKey(key: string): string {
        // Firestore reserves field names that begin and end with "__".
        // We store the first-scene key as "start"; map it back to the internal "__start__".
        if (key === "start") return "__start__";
        return key;
    }

    private static readonly SUPPORTED_SCHEMA_VERSION = 1;

    static async restoreFromFirestore(args: {
        compositionId: string;
        resolveAssetUrl: ResolveAssetUrlFn;
        resolveEnhancedAudioUrl?: ResolveEnhancedAudioUrlFn;
    }): Promise<void> {
        if (!CompositionService.isInitialized()) {
            console.warn("[CompositionRestoreService] Cannot restore: composition not initialized");
            return;
        }

        const comp = CompositionService.composition;

        const [meta, { layers }] = await Promise.all([
            CompositionPersistenceService.fetchComposition(args.compositionId),
            CompositionPersistenceService.fetchClipsByLayerOrdered(args.compositionId),
        ]);

        await this.restoreIntoCompositionFromSnapshot({
            comp,
            meta,
            layers: (layers as PersistedLayer[]) ?? [],
            resolveAssetUrl: args.resolveAssetUrl,
            resolveEnhancedAudioUrl: args.resolveEnhancedAudioUrl,
            writeToStateStore: true,
        });
    }

    static async restoreIntoCompositionFromSnapshot(args: {
        comp: any;
        meta: any | null;
        layers: PersistedLayer[];
        resolveAssetUrl: ResolveAssetUrlFn;
        resolveEnhancedAudioUrl?: ResolveEnhancedAudioUrlFn;
        writeToStateStore?: boolean;
    }): Promise<void> {
        const writeToStateStore = args.writeToStateStore !== false;
        const comp = args.comp;

        if (args.meta) {
            if (
                typeof (args.meta as any).schemaVersion === "number" &&
                (args.meta as any).schemaVersion > this.SUPPORTED_SCHEMA_VERSION
            ) {
                console.warn(
                    `[CompositionRestoreService] Persisted schemaVersion ${(args.meta as any).schemaVersion} is newer than supported ${this.SUPPORTED_SCHEMA_VERSION}; attempting best-effort restore.`
                );
            }
            try {
                if (args.meta.backgroundColor) {
                    comp.changeBackground(args.meta.backgroundColor as any);
                }
            } catch {}
            try {
                const w = Number(args.meta.canvasWidth ?? 1920);
                const h = Number(args.meta.canvasHeight ?? 1080);
                if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
                    const snapped = snapCanvasSizeToPreset(w, h);
                    comp.resize(snapped.width, snapped.height);
                }
            } catch {}
        }

        const layers = Array.isArray(args.layers) ? args.layers : [];
        if (layers.length === 0) {
            return;
        }

        for (const layerData of layers as PersistedLayer[]) {
            const layerId = String(layerData.layerId);
            const clipRecords = Array.isArray(layerData.clips) ? layerData.clips : [];

            // Ensure empty layers are restored too.
            if (clipRecords.length === 0) {
                try {
                    comp.createTrack("base" as any, layerId);
                } catch {}
                continue;
            }

            let track: any | null = null;

            for (const rec of clipRecords) {
                const type = String(rec?.type ?? "").toLowerCase();
                try {
                    if (type === "script") {
                        const scriptClip = await this.createScriptClipFromRecord(rec, comp, args.resolveAssetUrl, {
                            writeToStateStore,
                        });
                        if (!scriptClip) continue;

                        // Insert into the script layer track (preserve trackId/layerId).
                        // @ts-ignore
                        await comp.insertClipIntoScriptLayer(scriptClip, layerId);
                        await this.applyStudioSoundIfNeeded(
                            scriptClip as any,
                            rec,
                            args.resolveAssetUrl,
                            args.resolveEnhancedAudioUrl
                        );
                        // Script layer clip always owns the layer; do not add other clips to this track.
                        continue;
                    }

                    const clip = await this.createClipFromRecord(rec, comp, args.resolveAssetUrl, {
                        writeToStateStore,
                    });
                    if (!clip) continue;

                    // Insert into composition (creates a track automatically if missing).
                    if (!track) {
                        track = await comp.add(clip as any, layerId);
                    } else {
                        await track.add(clip as any);
                    }
                    await this.applyStudioSoundIfNeeded(clip as any, rec, args.resolveAssetUrl, args.resolveEnhancedAudioUrl);
                } catch (e) {
                    console.error("[CompositionRestoreService] Failed to restore clip", e);
                }
            }
        }
    }

    private static async createClipFromRecord(
        rec: any,
        comp: any,
        resolveAssetUrl: ResolveAssetUrlFn,
        opts?: { writeToStateStore?: boolean }
    ): Promise<any | null> {
        const type = String(rec?.type ?? "").toLowerCase();
        const assetId = rec?.assetId ? String(rec.assetId) : "";
        const clipId = rec?.id ? String(rec.id) : undefined;
        const name = String(rec?.name ?? "");
        const thumbId = rec?.thumbnailId ? String(rec.thumbnailId) : undefined;

        let clip: any | null = null;

        if (type === "image") {
            if (!assetId) return null;
            const url = await resolveAssetUrl(assetId, "image");
            if (!url) return null;
            const ref: MediaSourceRef = { id: assetId, kind: "image", imgUrl: url } as any;
            clip = new ImageClip(
                ref,
                { position: { x: comp.width / 2, y: comp.height / 2 }, width: comp.width, height: comp.height } as any,
                clipId
            );
        } else if (type === "placeholder") {
            clip = new PlaceholderClip(
                { position: { x: comp.width / 2, y: comp.height / 2 }, width: comp.width / 2, height: comp.height / 2 } as any,
                clipId
            );
        } else if (type === "video" || type === "audio") {
            if (!assetId) return null;
            const kind = type === "video" ? "video" : "audio";
            const url = await resolveAssetUrl(assetId, kind);
            if (!url) return null;
            const source = new UrlSource(url);
            const input = new Input({ source, formats: ALL_FORMATS });
            const ref: MediaSourceRef = { id: assetId, kind, input } as any;
            if (type === "video") {
                clip = new VideoClip(
                    ref,
                    { position: { x: comp.width / 2, y: comp.height / 2 }, width: comp.width, height: comp.height } as any,
                    clipId
                );
            } else {
                clip = new AudioClip(ref, {}, clipId);
            }
        } else if (type === "captions") {
            const cfg = rec?.captions ?? {};
            clip = new CaptionsClip({
                scriptClipId: cfg?.scriptClipId ?? undefined,
                timingScope: cfg?.timingScope ?? undefined,
                displayMode: cfg?.displayMode ?? undefined,
                textAlign: cfg?.textAlign ?? undefined,
                textBaseline: cfg?.textBaseline ?? undefined,
                textCase: cfg?.textCase ?? undefined,
                maxWidth: cfg?.maxWidth ?? undefined,
                textStyle: cfg?.textStyle ?? undefined,
                highlightStyle: cfg?.highlightStyle ?? undefined,
                background: cfg?.background ?? undefined,
                phraseConfig: cfg?.phraseConfig ?? undefined,
                words: [],
                position: { x: "50%", y: "85%" },
            } as any);
            try {
                const sceneOverrides = cfg?.sceneStyleOverrides;
                if (sceneOverrides && typeof sceneOverrides === "object") {
                    const mapped: Record<string, any> = {};
                    for (const [k, v] of Object.entries(sceneOverrides)) {
                        if (!k || typeof k !== "string") continue;
                        mapped[this.fromFirestoreSceneKey(k)] = v;
                    }
                    (clip as any)._sceneStyleOverrides = mapped;
                }
            } catch {}
        } else if (type === "text") {
            const cfg = rec?.text ?? {};
            clip = new TextClip({
                text: cfg?.text ?? undefined,
                textAlign: cfg?.textAlign ?? undefined,
                textBaseline: cfg?.textBaseline ?? undefined,
                textCase: cfg?.textCase ?? undefined,
                maxWidth: cfg?.maxWidth ?? undefined,
                textStyle: cfg?.textStyle ?? undefined,
            } as any);
            try {
                const sceneOverrides = cfg?.sceneStyleOverrides;
                if (sceneOverrides && typeof sceneOverrides === "object") {
                    const mapped: Record<string, any> = {};
                    for (const [k, v] of Object.entries(sceneOverrides)) {
                        if (!k || typeof k !== "string") continue;
                        mapped[this.fromFirestoreSceneKey(k)] = v;
                    }
                    (clip as any)._sceneStyleOverrides = mapped;
                }
            } catch {}
        } else {
            return null;
        }

        if (!clip) return null;
        try { if (clipId) clip.id = clipId; } catch {}
        try { clip.name = name; } catch {}

        this.applyTimingAndProps(clip, rec);
        this.applyVisualProps(clip, rec);
        this.applyTransitions(clip, rec);
        this.applySceneVisualOverrides(clip, rec);
        this.applyPlaceholderConfig(clip, rec);

        if (opts?.writeToStateStore !== false) {
            // Track asset mapping for waveform/transcript lookups.
            try { if (assetId) CompositionStateStore.setAssetIdForClip(clip.id, assetId); } catch {}
            try { if (thumbId) CompositionStateStore.setThumbnailIdForClip(clip.id, thumbId); } catch {}
            try { if (name) CompositionStateStore.setTitleForClip(clip.id, name); } catch {}
        }

        if (clip.type === "text") {
            console.log("Logging clip being restored: ", clip.text);
        }

        return clip;
    }

    private static async applyStudioSoundIfNeeded(
        clip: any,
        rec: any,
        resolveAssetUrl: ResolveAssetUrlFn,
        resolveEnhancedAudioUrl?: ResolveEnhancedAudioUrlFn
    ): Promise<void> {
        if (!clip || typeof clip.setStudioSound !== "function") return;
        if (!rec?.studioSoundEnabled) return;
        if (!resolveEnhancedAudioUrl) return;

        const type = String(rec?.type ?? "").toLowerCase();

        try {
            if (type === "script") {
                const sources = Array.isArray(rec?.sources) ? rec.sources : [];
                const enhancedUrls = new Map<string, string>();
                const originalUrls = new Map<string, string>();

                for (const src of sources) {
                    const assetId = src?.assetId ? String(src.assetId) : "";
                    if (!assetId) continue;

                    try {
                        const enhanced = await resolveEnhancedAudioUrl(assetId);
                        if (enhanced) enhancedUrls.set(assetId, enhanced);
                    } catch {}

                    try {
                        const original =
                            (await resolveAssetUrl(assetId, "video")) ||
                            (await resolveAssetUrl(assetId, "audio"));
                        if (original) originalUrls.set(assetId, original);
                    } catch {}
                }

                if (enhancedUrls.size === 0) return;
                await clip.setStudioSound(true, enhancedUrls, originalUrls);
                return;
            }

            if (type === "audio" || type === "video") {
                const assetId = rec?.assetId ? String(rec.assetId) : "";
                if (!assetId) return;

                const enhanced = await resolveEnhancedAudioUrl(assetId);
                if (!enhanced) return;

                const original = await resolveAssetUrl(assetId, type === "video" ? "video" : "audio");
                await clip.setStudioSound(true, enhanced, original);
            }
        } catch (e) {
            console.warn("[CompositionRestoreService] Failed to apply Studio Sound:", e);
        }
    }

    private static async createScriptClipFromRecord(
        rec: any,
        comp: any,
        resolveAssetUrl: ResolveAssetUrlFn,
        opts?: { writeToStateStore?: boolean }
    ): Promise<ScriptLayerClip | null> {
        const clipId = rec?.id ? String(rec.id) : undefined;
        const name = String(rec?.name ?? "");
        const sources = Array.isArray(rec?.sources) ? rec.sources : [];
        if (!sources.length) return null;
        const trackId = rec?.assetId ? String(rec.assetId) : "";
        const thumbId = rec?.thumbnailId ? String(rec.thumbnailId) : undefined;

	        const inputConfigs: ScriptLayerInputConfig[] = [];
	        for (const src of sources) {
            const assetId = src?.assetId ? String(src.assetId) : "";
            if (!assetId) continue;

            const url = await resolveAssetUrl(assetId, "video");
            if (!url) continue;

            const input = new Input({ source: new UrlSource(url), formats: ALL_FORMATS });
	            const segments: ScriptLayerSourceSegment[] | undefined = Array.isArray(src?.segments)
	                ? (src.segments as any[])
	                      .map((s: any) => {
	                          const segId = s?.id ? String(s.id) : crypto?.randomUUID?.() ?? `seg_${Date.now()}_${Math.random()}`;
	                          const offsetSec = Number(s?.offsetSec ?? 0);
	                          const playbackRateRaw = s?.playbackRate;
	                          const playbackRate = Number.isFinite(playbackRateRaw) ? Number(playbackRateRaw) : undefined;
	                          const inSec = Number(s?.range?.inSec ?? NaN);
	                          const outSec = Number(s?.range?.outSec ?? NaN);
	                          if (!Number.isFinite(offsetSec) || !Number.isFinite(inSec) || !Number.isFinite(outSec) || outSec <= inSec) return null;
	                          return {
	                              id: segId,
	                              offsetSec,
	                              playbackRate,
	                              range: { inSec, outSec },
	                          } as ScriptLayerSourceSegment;
	                      })
	                      .filter(Boolean)
	                : undefined;

	            inputConfigs.push({
	                segments,
	                input,
	                assetId,
	                name: src?.name ?? "",
	                color: src?.color ?? undefined,
	                gain: Number.isFinite(src?.gain) ? Number(src.gain) : 1.0,
	                playbackRate: Number.isFinite(src?.playbackRate) ? Number(src.playbackRate) : 1.0,
	                offsetSec: Number.isFinite(src?.offsetSec) ? Number(src.offsetSec) : 0,
	                range: src?.range ? { inSec: Number(src.range.inSec ?? 0), outSec: Number(src.range.outSec ?? 0) } : undefined,
	                visual: src?.visual
	                    ? ({
	                          x: Number(src.visual.x ?? 0),
	                          y: Number(src.visual.y ?? 0),
	                          width: Number(src.visual.width ?? 0),
	                          height: Number(src.visual.height ?? 0),
	                          alpha: Number(src.visual.alpha ?? 1),
	                          maskShape: typeof src.visual.maskShape === "string" ? String(src.visual.maskShape) : undefined,
	                          rotation: Number.isFinite(src.visual.rotation) ? Number(src.visual.rotation) : undefined,
	                          originalWidth: Number.isFinite(src.visual.originalWidth) ? Number(src.visual.originalWidth) : undefined,
	                          originalHeight: Number.isFinite(src.visual.originalHeight) ? Number(src.visual.originalHeight) : undefined,
	                          crop: src.visual.crop
	                              ? {
	                                    x: Number(src.visual.crop.x ?? 0),
	                                    y: Number(src.visual.crop.y ?? 0),
	                                    width: Number(src.visual.crop.width ?? 1),
	                                    height: Number(src.visual.crop.height ?? 1),
	                                }
	                              : undefined,
	                      } as any)
	                    : undefined,
                layerIndex: Number.isFinite(src?.layerIndex) ? Number(src.layerIndex) : undefined,
            });
        }

        if (!inputConfigs.length) return null;

        const scriptClip = ScriptLayerClip.fromInputConfigs(
            inputConfigs,
            {
                position: { x: comp.width / 2, y: comp.height / 2 },
                width: comp.width,
                height: comp.height,
            } as any,
            clipId
        );

        try { scriptClip.name = name; } catch {}

        this.applyTimingAndProps(scriptClip as any, rec);
        this.applyVisualProps(scriptClip as any, rec);

        if (opts?.writeToStateStore !== false) {
            // Track asset mapping for transcript lookups.
            try {
                const fallbackTrackId = sources?.[0]?.assetId ? String(sources[0].assetId) : "";
                const idForTranscript = trackId || fallbackTrackId;
                if (idForTranscript) CompositionStateStore.setAssetIdForClip(scriptClip.id, idForTranscript);
            } catch {}
            try { if (thumbId) CompositionStateStore.setThumbnailIdForClip(scriptClip.id, thumbId); } catch {}
            try { if (name) CompositionStateStore.setTitleForClip(scriptClip.id, name); } catch {}
        }

        // Restore script edits (cuts/gaps/splits).
        try {
            const cuts: ScriptLayerCut[] = Array.isArray(rec.scriptCuts) ? rec.scriptCuts : [];
            const gaps: ScriptLayerGap[] = Array.isArray(rec.gaps) ? rec.gaps : [];
            const gapInsertions: ScriptLayerGapInsertion[] = Array.isArray((rec as any).gapInsertions) ? (rec as any).gapInsertions : [];
            const splits: ScriptLayerSplit[] = Array.isArray(rec.splits) ? rec.splits : [];
            const speakerOverrides: SpeakerOverride[] = Array.isArray(rec.speakerOverrides) ? rec.speakerOverrides : [];
            const speakerMuteRanges: SpeakerMuteRange[] = Array.isArray((rec as any).speakerMuteRanges) ? (rec as any).speakerMuteRanges : [];
            const transcriptHiddenRanges: any[] = Array.isArray((rec as any).transcriptHiddenRanges) ? (rec as any).transcriptHiddenRanges : [];
            const wordCorrections: any[] = Array.isArray((rec as any).wordCorrections) ? (rec as any).wordCorrections : [];
            const transcriptParagraphBreaks: any[] = Array.isArray((rec as any).transcriptParagraphBreaks)
                ? (rec as any).transcriptParagraphBreaks
                : [];
            const transcriptMarkers: any[] = Array.isArray((rec as any).transcriptMarkers)
                ? (rec as any).transcriptMarkers
                : [];
            // @ts-ignore - method added for persistence restore
            scriptClip.applyPersistedEdits?.({
                scriptCuts: cuts,
                gaps,
                gapInsertions,
                splits,
                speakerOverrides,
                speakerMuteRanges,
                transcriptHiddenRanges,
                wordCorrections,
                transcriptParagraphBreaks,
                transcriptMarkers,
            });
        } catch {}

        // Restore per-scene overrides (gain/playbackRate/layout/transitions/visual overrides).
        try {
            const sceneProps = Array.isArray(rec.sceneProperties) ? rec.sceneProperties : [];
            this.applySceneProperties(scriptClip, sceneProps);
        } catch {}

        // Restore automatic multicam camera setup (optional).
        try {
            const setup = (rec as any)?.multicamCameraSetup;
            if (setup && typeof setup === "object") {
                const assignmentsRaw = Array.isArray((setup as any).assignments) ? (setup as any).assignments : [];
                const cleaned: any[] = [];
                for (const a of assignmentsRaw) {
                    const sourceIndex = Number((a as any)?.sourceIndex);
                    if (!Number.isFinite(sourceIndex) || sourceIndex < 0) continue;
                    const kind = String((a as any)?.kind ?? "").trim();
                    if (kind !== "speaker" && kind !== "shared" && kind !== "unassigned") continue;
                    const recA: any = { sourceIndex, kind };
                    const speakerId = String((a as any)?.speakerId ?? "").trim();
                    if (kind === "speaker" && speakerId) recA.speakerId = speakerId;
                    cleaned.push(recA);
                }
                cleaned.sort((a, b) => Number(a?.sourceIndex ?? 0) - Number(b?.sourceIndex ?? 0));
                (scriptClip as any).multicamCameraSetup =
                    cleaned.length > 0 ? { version: 1, assignments: cleaned } : null;
            } else {
                (scriptClip as any).multicamCameraSetup = null;
            }
        } catch {}

        return scriptClip;
    }

    private static applySceneProperties(scriptClip: ScriptLayerClip, sceneProps: any[]): void {
        if (!Array.isArray(sceneProps) || sceneProps.length === 0) return;

        for (const sp of sceneProps) {
            const sceneIndex = Number(sp?.sceneIndex);
            if (!Number.isFinite(sceneIndex) || sceneIndex < 0) continue;

            const props: any = {};

            if (Number.isFinite(sp?.gain)) props.gain = Number(sp.gain);
            if (Number.isFinite(sp?.playbackRate)) props.playbackRate = Number(sp.playbackRate);
            if (typeof sp?.backgroundColor === "string" && sp.backgroundColor.trim()) {
                props.backgroundColor = String(sp.backgroundColor).trim();
            }
            // Per-source audio gain multipliers
            try {
                const ag = (sp as any)?.audioSourceGains;
                if (Array.isArray(ag) && ag.length > 0) {
                    const m = new Map<number, number>();
                    for (const entry of ag) {
                        const sourceIndex = Number((entry as any)?.sourceIndex);
                        const gain = Number((entry as any)?.gain);
                        if (!Number.isFinite(sourceIndex) || sourceIndex < 0) continue;
                        if (!Number.isFinite(gain)) continue;
                        m.set(sourceIndex, gain);
                    }
                    if (m.size > 0) props.audioSourceGains = m;
                }
            } catch {}
            if (typeof sp?.layout === "string") props.layout = sp.layout;
            if (sp?.layoutByAspect && typeof sp.layoutByAspect === "object") {
                const cleaned: any = {};
                for (const key of ["horizontal", "vertical", "square"]) {
                    if (!Object.prototype.hasOwnProperty.call(sp.layoutByAspect, key)) continue;
                    const v = (sp.layoutByAspect as any)[key];
                    if (v === null) cleaned[key] = null;
                    else if (typeof v === "string" && v) cleaned[key] = v;
                }
                if (Object.keys(cleaned).length > 0) props.layoutByAspect = cleaned;
            }

            if (sp?.inTransition) props.inTransition = this.parseTransition(sp.inTransition);
            if (sp?.outTransition) props.outTransition = this.parseTransition(sp.outTransition);

            const vo = sp?.visualOverrides;
            if (Array.isArray(vo) && vo.length > 0) {
                const m = new Map<number, any>();
                for (const entry of vo) {
                    const sourceIndex = Number(entry?.sourceIndex);
                    if (!Number.isFinite(sourceIndex) || sourceIndex < 0) continue;
                    const v: any = {};
                    if (Number.isFinite(entry?.alpha)) v.alpha = Number(entry.alpha);
                    if (typeof (entry as any)?.hiddenByUser === "boolean" && (entry as any).hiddenByUser === true) {
                        v.hiddenByUser = true;
                    }
                    if (Number.isFinite(entry?.x)) v.x = Number(entry.x);
                    if (Number.isFinite(entry?.y)) v.y = Number(entry.y);
                    if (Number.isFinite(entry?.width)) v.width = Number(entry.width);
                    if (Number.isFinite(entry?.height)) v.height = Number(entry.height);
                    try {
                        const crop = (entry as any)?.crop;
                        if (crop && typeof crop === "object") {
                            const x = Number((crop as any).x);
                            const y = Number((crop as any).y);
                            const width = Number((crop as any).width);
                            const height = Number((crop as any).height);
                            if ([x, y, width, height].every(Number.isFinite) && width > 0 && height > 0) {
                                v.crop = { x, y, width, height };
                            }
                        }
                    } catch {}
                    if (typeof entry?.maskShape === "string") v.maskShape = String(entry.maskShape);
                    if (Object.keys(v).length > 0) m.set(sourceIndex, v);
                }
                if (m.size > 0) props.visualOverrides = m;
            }

            const voByAspect = (sp as any)?.visualOverridesByAspect;
            if (voByAspect && typeof voByAspect === "object") {
                const outByAspect: any = {};
                for (const [presetKey, arr] of Object.entries(voByAspect as any)) {
                    if (!Array.isArray(arr) || arr.length === 0) continue;
                    const m = new Map<number, any>();
                    for (const entry of arr as any[]) {
                        const sourceIndex = Number((entry as any)?.sourceIndex);
                        if (!Number.isFinite(sourceIndex) || sourceIndex < 0) continue;
                        const v: any = {};
                        if (Number.isFinite((entry as any)?.alpha)) v.alpha = Number((entry as any).alpha);
                        if (typeof (entry as any)?.hiddenByUser === "boolean" && (entry as any).hiddenByUser === true) {
                            v.hiddenByUser = true;
                        }
                        if (Number.isFinite((entry as any)?.x)) v.x = Number((entry as any).x);
                        if (Number.isFinite((entry as any)?.y)) v.y = Number((entry as any).y);
                        if (Number.isFinite((entry as any)?.width)) v.width = Number((entry as any).width);
                        if (Number.isFinite((entry as any)?.height)) v.height = Number((entry as any).height);
                        try {
                            const crop = (entry as any)?.crop;
                            if (crop && typeof crop === "object") {
                                const x = Number((crop as any).x);
                                const y = Number((crop as any).y);
                                const width = Number((crop as any).width);
                                const height = Number((crop as any).height);
                                if ([x, y, width, height].every(Number.isFinite) && width > 0 && height > 0) {
                                    v.crop = { x, y, width, height };
                                }
                            }
                        } catch {}
                        if (typeof (entry as any)?.maskShape === "string") v.maskShape = String((entry as any).maskShape);
                        if (Object.keys(v).length > 0) m.set(sourceIndex, v);
                    }
                    if (m.size > 0) outByAspect[String(presetKey)] = m;
                }
                if (Object.keys(outByAspect).length > 0) props.visualOverridesByAspect = outByAspect;
            }

            if (Object.keys(props).length > 0) {
                scriptClip.setSceneProperties(sceneIndex, props);
            }
        }
    }

    private static parseTransition(t: any): any | undefined {
        if (!t) return undefined;
        const out: any = {};
        if (t.audio && typeof t.audio.type === "string") {
            out.audio = {
                type: t.audio.type,
                durationMs: Number(t.audio.durationMs ?? 0),
            };
        }
        if (t.video && typeof t.video.type === "string") {
            out.video = {
                type: t.video.type,
                durationMs: Number(t.video.durationMs ?? 0),
                easing: typeof (t.video as any)?.easing === "string" ? String((t.video as any).easing) : undefined,
                addFades: typeof (t.video as any)?.addFades === "boolean" ? Boolean((t.video as any).addFades) : undefined,
            };
        }
        return Object.keys(out).length > 0 ? out : undefined;
    }

    private static applyTimingAndProps(clip: any, rec: any): void {
        try {
            if ("range" in clip && "offset" in clip) {
                const inSec = Number(rec?.inSec ?? 0);
                const outSec = Number(rec?.outSec ?? inSec);
                const offsetSec = Number(rec?.offsetSec ?? 0);
                clip.range = [Timestamp.fromSeconds(inSec), Timestamp.fromSeconds(outSec)];
                clip.offset = Timestamp.fromSeconds(offsetSec);
            } else {
                // Non-media clips store timing as startSec + durationSec.
                const startSec = Number(rec?.startSec ?? 0);
                const durSec = Number(rec?.durationSec ?? 0);
                if (Number.isFinite(startSec) && Number.isFinite(durSec) && durSec >= 0) {
                    clip.start = Timestamp.fromSeconds(startSec);
                    clip.stop = Timestamp.fromSeconds(startSec + durSec);
                }
            }
        } catch {}

        try {
            const pr = Number(rec?.props?.playbackRate ?? 1);
            if (typeof clip.playbackRate === "number" || clip.playbackRate === undefined) {
                clip.playbackRate = Number.isFinite(pr) ? pr : 1;
            }
        } catch {}
        try {
            const g = Number(rec?.props?.gain ?? 1);
            if (typeof clip.gain === "number" || clip.gain === undefined) {
                clip.gain = Number.isFinite(g) ? g : 1;
            }
        } catch {}
        try {
            const a = Number(rec?.props?.alpha ?? 1);
            if ("alpha" in clip) {
                clip.alpha = Number.isFinite(a) ? a : 1;
            }
        } catch {}
    }

    private static applyVisualProps(clip: any, rec: any): void {
        const v = rec?.visual;
        if (!v) return;

        const type = String((clip as any)?.type ?? "");
        const isCaptions = type === "captions";
        const isText = type === "text";
        const widthFromRec = typeof v.width === "number" ? v.width : undefined;
        const heightFromRec = typeof v.height === "number" ? v.height : undefined;
        const hasExplicitSize =
            !isCaptions &&
            !isText &&
            ((typeof widthFromRec === "number" && Number.isFinite(widthFromRec) && widthFromRec > 0) ||
                (typeof heightFromRec === "number" && Number.isFinite(heightFromRec) && heightFromRec > 0));
        const scaleIsRelative = v?.scaleIsRelative === true;

        try {
            if (typeof (clip as any).setCrop === "function" && v.crop) {
                (clip as any).setCrop({
                    x: Number(v.crop.x ?? 0),
                    y: Number(v.crop.y ?? 0),
                    width: Number(v.crop.width ?? 1),
                    height: Number(v.crop.height ?? 1),
                });
            }
        } catch {}

        try {
            const x = typeof v.x === "number" ? v.x : undefined;
            const y = typeof v.y === "number" ? v.y : undefined;
            if (typeof x === "number" && typeof y === "number" && Number.isFinite(x) && Number.isFinite(y)) {
                // Prefer position for VisualMixin clips
                if ("position" in clip) clip.position = { x, y };
                else {
                    if ("x" in clip) clip.x = x;
                    if ("y" in clip) clip.y = y;
                }
            }
        } catch {}

        try {
            if (!isCaptions && !isText) {
                if (typeof widthFromRec === "number" && Number.isFinite(widthFromRec) && widthFromRec > 0) clip.width = widthFromRec;
                if (typeof heightFromRec === "number" && Number.isFinite(heightFromRec) && heightFromRec > 0) clip.height = heightFromRec;
            }
        } catch {}

        try {
            const r = typeof v.rotation === "number" ? v.rotation : undefined;
            if (!isCaptions && typeof r === "number" && Number.isFinite(r) && "rotation" in clip) clip.rotation = r;
        } catch {}

        try {
            const ms = typeof v.maskShape === "string" ? v.maskShape : undefined;
            if (!isCaptions && !isText && typeof ms === "string" && ms && "maskShape" in clip) {
                (clip as any).maskShape = ms;
            }
        } catch {}

        try {
            const a = typeof v.alpha === "number" ? v.alpha : undefined;
            if (typeof a === "number" && Number.isFinite(a) && "alpha" in clip) clip.alpha = a;
        } catch {}

        try {
            const sx = typeof v.scaleX === "number" ? v.scaleX : undefined;
            const sy = typeof v.scaleY === "number" ? v.scaleY : undefined;
            // Avoid double-applying scale when explicit width/height were persisted.
            // (VisualMixin treats `scale` as an additional multiplier on top of width/height sizing.)
            if (!isCaptions && !isText && (!hasExplicitSize || scaleIsRelative)) {
                if (typeof sx === "number" && typeof sy === "number" && Number.isFinite(sx) && Number.isFinite(sy) && "scale" in clip) {
                    clip.scale = { x: sx, y: sy };
                }
            }
        } catch {}

        try { clip.trigger?.("frame", undefined); } catch {}
    }

    private static applyTransitions(clip: any, rec: any): void {
        if (!clip) return;
        if (!rec || typeof rec !== "object") return;

        const parseFade = (cfg: any): any | undefined => {
            if (!cfg || typeof cfg.type !== "string") return undefined;
            return {
                type: String(cfg.type),
                durationMs: Number(cfg.durationMs ?? 0),
            };
        };

        const parseVisualFade = (cfg: any): any | undefined => {
            if (!cfg || typeof cfg.type !== "string") return undefined;
            const out: any = {
                type: String(cfg.type),
                durationMs: Number(cfg.durationMs ?? 0),
            };
            if (typeof cfg.wipeDirection === "string") out.wipeDirection = cfg.wipeDirection;
            if (Number.isFinite(cfg.blurAmount)) out.blurAmount = Number(cfg.blurAmount);
            return out;
        };

        try {
            if ("fadeIn" in rec) {
                const fi = parseFade(rec?.fadeIn);
                clip.fadeIn = fi ?? undefined;
            }
        } catch {}

        try {
            if ("fadeOut" in rec) {
                const fo = parseFade(rec?.fadeOut);
                clip.fadeOut = fo ?? undefined;
            }
        } catch {}

        try {
            if ("visualFadeIn" in rec) {
                const vfi = parseVisualFade(rec?.visualFadeIn);
                clip.visualFadeIn = vfi ?? undefined;
            }
        } catch {}

        try {
            if ("visualFadeOut" in rec) {
                const vfo = parseVisualFade(rec?.visualFadeOut);
                clip.visualFadeOut = vfo ?? undefined;
            }
        } catch {}

        try {
            if (Array.isArray(rec?.transitionPoints)) {
                const pts = rec.transitionPoints
                    .map((tp: any) => {
                        const id = String(tp?.id ?? "");
                        const srcTimeSec = Number(tp?.srcTimeSec ?? Number.NaN);
                        if (!id || !Number.isFinite(srcTimeSec)) return null;

                        const fadeOut = parseFade(tp?.transition?.fadeOut);
                        const fadeIn = parseFade(tp?.transition?.fadeIn);
                        const transition = fadeOut || fadeIn ? { fadeOut, fadeIn } : undefined;

                        return { id, srcTimeSec, transition };
                    })
                    .filter(Boolean)
                    .sort((a: any, b: any) => (a.srcTimeSec ?? 0) - (b.srcTimeSec ?? 0));

                clip.transitionPoints = pts;
            }
        } catch {}

        try { clip.trigger?.("frame", undefined); } catch {}
    }

    private static applyPlaceholderConfig(clip: any, rec: any): void {
        try {
            const type = String((clip as any)?.type ?? "");
            if (type !== "placeholder") return;
            if (!rec || typeof rec !== "object") return;

            const cfg = (rec as any)?.placeholder ?? {};

            const label = typeof cfg?.label === "string" ? cfg.label : undefined;
            if (label !== undefined) {
                try { (clip as any).label = label; } catch {}
            }

            const kindRaw = typeof cfg?.placeholderKind === "string" ? cfg.placeholderKind : undefined;
            let kind = kindRaw;
            if (!kind) {
                const name = String((rec as any)?.name ?? (clip as any)?.name ?? "");
                if (/screen/i.test(name)) kind = "screen";
                else if (/camera/i.test(name)) kind = "camera";
                else kind = "media";
            }
            try { (clip as any).placeholderKind = kind; } catch {}
            try { clip.trigger?.("frame", undefined); } catch {}
        } catch {}
    }

    private static applySceneVisualOverrides(clip: any, rec: any): void {
        if (!clip || !rec || typeof rec !== "object") return;
        const ovsByAspect = (rec as any)?.sceneVisualOverridesByAspect;
        if (ovsByAspect && typeof ovsByAspect === "object") {
            try {
                const cleanedByAspect: Record<string, any> = {};
                for (const presetKey of ["horizontal", "vertical", "square"]) {
                    const ovs = (ovsByAspect as any)[presetKey];
                    if (!ovs || typeof ovs !== "object") continue;
                    const cleaned: Record<string, any> = {};
                    for (const [k, v] of Object.entries(ovs)) {
                        if (!k || typeof k !== "string") continue;
                        if (!v || typeof v !== "object") continue;
                        const out: any = {};
                        for (const key of ["x", "y", "width", "height", "alpha", "rotation"]) {
                            const num = (v as any)[key];
                            if (typeof num === "number" && Number.isFinite(num)) out[key] = num;
                        }
                        if (typeof (v as any)?.hiddenByUser === "boolean" && (v as any).hiddenByUser === true) {
                            out.hiddenByUser = true;
                        }
                        if (typeof (v as any).maskShape === "string") out.maskShape = String((v as any).maskShape);
                        try {
                            const crop = (v as any)?.crop;
                            if (crop && typeof crop === "object") {
                                const x = Number((crop as any).x);
                                const y = Number((crop as any).y);
                                const width = Number((crop as any).width);
                                const height = Number((crop as any).height);
                                if ([x, y, width, height].every(Number.isFinite) && width > 0 && height > 0) {
                                    out.crop = { x, y, width, height };
                                }
                            }
                        } catch {}
                        if (Object.keys(out).length > 0) cleaned[this.fromFirestoreSceneKey(k)] = out;
                    }
                    if (Object.keys(cleaned).length > 0) cleanedByAspect[presetKey] = cleaned;
                }
                if (Object.keys(cleanedByAspect).length > 0) {
                    (clip as any).sceneVisualOverridesByAspect = cleanedByAspect;
                }
            } catch {}
            return;
        }

        // Legacy fallback
        const ovs = (rec as any)?.sceneVisualOverrides;
        if (!ovs || typeof ovs !== "object") return;
        try {
            const cleaned: Record<string, any> = {};
            for (const [k, v] of Object.entries(ovs)) {
                if (!k || typeof k !== "string") continue;
                if (!v || typeof v !== "object") continue;
                const out: any = {};
                for (const key of ["x", "y", "width", "height", "alpha", "rotation"]) {
                    const num = (v as any)[key];
                    if (typeof num === "number" && Number.isFinite(num)) out[key] = num;
                }
                if (typeof (v as any)?.hiddenByUser === "boolean" && (v as any).hiddenByUser === true) {
                    out.hiddenByUser = true;
                }
                if (typeof (v as any).maskShape === "string") out.maskShape = String((v as any).maskShape);
                if (Object.keys(out).length > 0) cleaned[this.fromFirestoreSceneKey(k)] = out;
            }
            if (Object.keys(cleaned).length > 0) {
                (clip as any).sceneVisualOverrides = cleaned;
            }
        } catch {}
    }
}
